<template>
  <v-row no-gutters>
    <v-col cols="12" md="9">
      <step-summary />
    </v-col>

    <v-col cols="12" md="3">
      <file-uploader
        v-if="showHouseConnectionUpload"
        ref="fileUploaderRef"
        label="Anschlussauftrag"
        :max-width="750"
        @file-set="setHouseConnectionDocument"
        @download-file="downloadFieldHouseConnectionContract"
      />

      <file-uploader
        v-if="showProductUpload"
        ref="fileUploaderRef"
        class="mt-4"
        label="Produktauftrag"
        :max-width="750"
        @file-set="setProductDocument"
        @download-file="downloadFieldProductContract"
      />

      <v-form ref="lastStepForm" class="mt-4 pa-3" max-750>
        <template v-if="orderStore.isOrderingNe4">
          <v-text-field
            v-model="orderStore.salesPartnerEmailAddress"
            :rules="buildRulesEMail(true)"
            maxlength="255"
            validate-on="input"
          >
            <template #label>
              <span>
                E-Mail-Adresse des Vertriebspartners<sup class="required">*</sup>
              </span>
            </template>
          </v-text-field>

          <v-text-field
            v-model="orderStore.netzLeadId"
            :rules="mandatory"
            :v-maska="netzLeadIdMask"
            maxlength="255"
            validate-on="input"
          >
            <template #prepend-inner>
              <span style="color: grey;width: 62px">{{ orderStore.netzLeadIdPrefix }}</span>
            </template>
          </v-text-field>
        </template>

        <v-text-field
          v-if="isAllowedD2dInput && !isMicromarketInPreparation"
          v-model="externalOrderNumber"
          :rules="externalOrderNumber.length ? externalOrderNumberRule : []"
          maxlength="12"
          label="Externe Auftragsnummer"
          validate-on="input"
          @change="updateButtonState"
        />

        <v-checkbox
          v-if="(isAllowedD2dInput || isAllowedPostRecording) && isEeccRelevant"
          v-model="uploadEeccDocuments"
          label="Nacherfassung eines EECC-Abschlusses"
          color="primary"
          hide-details
          @change="updateButtonState"
        />

        <v-checkbox
          v-if="uploadEeccDocuments && isAllowedPostRecording"
          v-model="confirmedByBackOffice"
          label="Nachtrag EECC-AngebotsID"
          color="primary"
          hide-details
          @change="eeccIdCheckboxChanged"
        />

        <v-text-field
          v-if="confirmedByBackOffice"
          v-model="eeccOrderId"
          :rules="checkEeccId"
          maxlength="9"
          label="EECC-AngebotsID"
          validate-on="input"
          @change="updateButtonState"
        />

        <v-checkbox
          v-if="showCollectLaterOption && isAllowedManualPostRecording"
          v-model="collectLater"
          label="Manuell Nacherfassen"
          color="primary"
          hide-details
        />

        <v-text-field
          v-if="collectLater"
          v-model="collectLaterId"
          :rules="mandatory"
          maxlength="100"
          label="NE4-Autrags-ID"
          @change="updateButtonState"
        />
      </v-form>
    </v-col>
  </v-row>

  <pop-up-dialog v-if="showInfoDialog" :configs="infoDialogConfigs" @close="closePopUp()" />
</template>

<script setup lang="ts">
import type { MaskInputOptions } from 'maska';
import {
  computed, inject, reactive, ref,
} from 'vue';
import type { VForm } from 'vuetify/components';
import preparePassiveOrderFields from '@/composables/prepare-house-connection-contract-data';
import prepareProductContractData from '@/composables/prepare-product-contract-data';
import { checkUserRight } from '@/composables/user';
import {
  externalOrderNumberRule, checkEeccId, mandatory, buildRulesEMail,
} from '@/composables/validations';
import { UpdateFinishButtonKey } from '@/configs/symbols';
import { downloadContract } from '@/services/api';
import useBookingStore from '@/store/booking';
import useOrderStore from '@/store/order';
import useInitStore from '@/store/init';
import { BookingMode, type Order, type UpdateFinishButtonFunc } from '@/types/order-main';
import {
  RIGHT_GVM_AUFTRAEGE_MANUELL_NACHERFASSEN,
  RIGHT_GVM_D2D_AUFTRAGSNUMMER_ERFASSEN,
  RIGHT_GVM_NACHERFASSUNGSMOEGLICHKEIT,
} from '@/types/user';
import fileUploader from './common/file-uploader.vue';
import popUpDialog from './pop-up-dialog.vue';
import StepSummary from './step-summary.vue';

const bookingStore = useBookingStore();
const orderStore = useOrderStore();
const initStore = useInitStore();

const updateFinishButton = inject<UpdateFinishButtonFunc>(UpdateFinishButtonKey)!;

const fileUploaderRef = ref();
const showInfoDialog = ref<boolean>(false);
const infoDialogConfigs = ref();
const collectLater = ref<boolean>(false);
const collectLaterId = ref(null);

const lastStepForm = ref<VForm>();
const uploadEeccDocuments = ref(false);
const confirmedByBackOffice = ref(false);
const externalOrderNumber = ref('');
const eeccOrderId = ref('');

const netzLeadIdMask = reactive<MaskInputOptions>({
  mask: '#####',
  eager: true,
});

const isMicromarketInPreparation = computed<boolean>(
  () => initStore.bookingApproach!.micromarketStatus === 'Vorbereitung',
);

const isAllowedD2dInput = computed<boolean>(
  () => checkUserRight(initStore.currentUser!, RIGHT_GVM_D2D_AUFTRAGSNUMMER_ERFASSEN),
);
const isAllowedPostRecording = computed<boolean>(
  () => checkUserRight(initStore.currentUser!, RIGHT_GVM_NACHERFASSUNGSMOEGLICHKEIT),
);
const isAllowedManualPostRecording = computed<boolean>(
  () => checkUserRight(initStore.currentUser!, RIGHT_GVM_AUFTRAEGE_MANUELL_NACHERFASSEN),
);

const isEeccRelevant = computed<boolean>(
  () => bookingStore.isCurrentBookingModeEeccRelevant && !isMicromarketInPreparation.value,
);

function closePopUp() {
  showInfoDialog.value = false;
  infoDialogConfigs.value = null;
}

const showUploads = computed<boolean>(
  () => !isEeccRelevant.value || (uploadEeccDocuments.value || confirmedByBackOffice.value),
);

const jsonPassiveOrderData = ref<string>();
const filePassiveOrder = ref<File>();

if (bookingStore.isCurrentBookingModeAnyHouseConnection || orderStore.isOrderingNe4) {
  jsonPassiveOrderData.value = preparePassiveOrderFields();
}

const jsonActiveOrderData = ref<string>();
const fileActiveOrder = ref<File>();

if (orderStore.isOrderingProduct) {
  jsonActiveOrderData.value = prepareProductContractData();
}

const showHouseConnectionUpload = computed<boolean>(() => {
  if (!bookingStore.isCurrentBookingModeAnyHouseConnection && !orderStore.isOrderingNe4) {
    return false;
  }
  if (showUploads.value || orderStore.isOrderingNe4) {
    return true;
  }
  return !isEeccRelevant.value;
});

const showCollectLaterOption = computed<boolean>(() => orderStore.isNe4OrderCompleteOrEndAssembly);

const showProductUpload = computed<boolean>(
  () => orderStore.isOrderingProduct && showUploads.value && !isMicromarketInPreparation.value,
);

function getLabelFinishButton() {
  return !isEeccRelevant.value || uploadEeccDocuments.value ? 'Abschließen' : 'Angebot fixieren';
}

const downloadFieldProductContract = async () => {
  const [error, url] = await downloadContract(
    4,
    initStore.addressId!,
    jsonActiveOrderData.value!,
  );
  if (url) {
    window.open(url, '_blank');
  }
  if (error) {
    infoDialogConfigs.value = {
      title: 'Fehler',
      message: error,
      closeText: 'Schließen',
      closeIcon: 'close-thick',
      type: 'error',
    };
    showInfoDialog.value = true;
  }
  fileUploaderRef.value?.resetLoading();
};

async function downloadFieldHouseConnectionContract() {
  let downloadTypeId = 1;
  if (orderStore.isOrderingNe4) {
    downloadTypeId = orderStore.ordererData?.household?.isCompany ? 3 : 2;
  }
  const [error, url] = await downloadContract(
    downloadTypeId,
    initStore.addressId!,
    jsonPassiveOrderData.value!,
  );
  if (url) {
    window.open(url, '_blank');
  }
  if (error) {
    infoDialogConfigs.value = {
      title: 'Fehler',
      message: error,
      closeText: 'Schließen',
      closeIcon: 'close-thick',
      type: 'error',
    };
    showInfoDialog.value = true;
  }
  fileUploaderRef.value?.resetLoading();
}

async function isValid() {
  const validations: boolean[] = [];
  if (lastStepForm.value) {
    const { valid } = await lastStepForm.value.validate();
    validations.push(valid);
  }
  if (showHouseConnectionUpload.value) {
    validations.push(!!filePassiveOrder.value);
  }
  if (showProductUpload.value) {
    validations.push(!!fileActiveOrder.value);
  }
  if (collectLater.value) {
    validations.push(collectLaterId.value !== null);
  }
  return validations.every(v => v);
}

async function updateButtonState() {
  const isFormValid = await isValid();
  updateFinishButton(getLabelFinishButton(), !isFormValid);
}

async function eeccIdCheckboxChanged() {
  eeccOrderId.value = '';
  await updateButtonState();
}

async function setHouseConnectionDocument(file?: File) {
  filePassiveOrder.value = file;
  await updateButtonState();
}

async function setProductDocument(file?: File) {
  fileActiveOrder.value = file;
  await updateButtonState();
}

async function getData(): Promise<{
  houseConnectionOrderDocument: File | undefined;
  productOrderDocument: File | undefined;
  orderParam: Order;
}> {
  return ({
    houseConnectionOrderDocument: filePassiveOrder.value,
    productOrderDocument: fileActiveOrder.value,
    orderParam: {
      confirmedByBackOffice: confirmedByBackOffice.value,
      eeccOrderId: eeccOrderId.value,
      externalOrderNumber: externalOrderNumber.value,
      hasHouseConnection: bookingStore.isCurrentBookingModeAnyHouseConnection,
      hasMph: orderStore.isOrderingNe4,
      hasProduct: orderStore.isOrderingProduct,
      isD2dConfirmed: uploadEeccDocuments.value,
      isEeccRelevant: isEeccRelevant.value,
      isMicromarketInPreparation: isMicromarketInPreparation.value,
      orderingType: BookingMode[bookingStore.currentBookingMode!],
      stepCustomerData: orderStore.customerData ?? null,
      stepMphData: orderStore.mphData ?? null,
      stepOrdererData: orderStore.ordererData,
      stepOrderRequestData: orderStore.orderRequestData,
      stepOwnerData: orderStore.ownerData ?? null,
      stepProductData: orderStore.productData ?? null,
      userId: initStore.currentUser!.id,
      productDocumentName: null,
      houseConnectionDocumentName: null,
      channel: initStore.params!.channel,
      contractNumber: 0, // TODO check if we have this info
      collectLater: collectLater.value,
      collectLaterId: collectLaterId.value,
      houseConnectionContractInfoAsJson: jsonPassiveOrderData.value,
      productContractInfoAsJson: jsonActiveOrderData.value,
      customerId: initStore.params!.customerId,
    } as Order,
  });
}

defineExpose({ isValid, getData });

isValid().then(valid => {
  updateFinishButton(getLabelFinishButton(), !valid);
});
</script>

<style scoped lang="scss">
  @use '../assets/sass/main';
</style>
